/* eslint-disable max-len */
// Centralized "How to use this module" content for every Panorix module.
// Each entry follows the MTM Help Drawer schema:
//   { moduleKey, title, subtitle, color, accent, tabs: [
//       { id, label, Icon, purpose, source, when, flow:[...steps], tips?:[...] }
//   ] }
// The same shape powers <ModuleHelpDrawer />.
import {
  BarChart3, LayoutGrid, Hammer, CableCar, Cog, FileUp, Box, ShieldCheck,
  ClipboardCheck, Repeat, ScanLine, Building2, ShieldAlert, TrendingDown, Settings, Wrench, Layers, Warehouse,
  Calendar, DollarSign, FileText, Briefcase, Users, AlertOctagon, GitBranch, Activity, Gauge,
  PieChart, Receipt, Banknote, Trophy, HardHat, Truck, Compass, Sparkles, Database, FileSpreadsheet,
  Bell, MapPin, Bot, Eye, Target, BookOpen, Shield, Map,
} from 'lucide-react';

// Helper builders to keep entries readable
const T = (id, label, Icon, purpose, source, when, flow, tips = []) => ({
  id, label, Icon, purpose, source, when, flow, tips,
});

export const MODULE_GUIDES = {
  // ─────────────────────────────────────────────────────────────────────────────
  // Piping Workfront Management (PWM)
  // ─────────────────────────────────────────────────────────────────────────────
  pwm: {
    moduleKey: 'pwm',
    title: 'Piping Workfront Allocation & Forecasting',
    subtitle: 'Spool/line lifecycle: Material → Fabrication (5 gates) → Erection (Inch-Meter)',
    color: '#143A6B',
    accent: '#1E4E8C',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Project-level snapshot of allocation health, fabrication & erection progress.',
        '`pwm_workfronts` collection + materials snapshot from `mtmMaterials` (inventory.* counters).',
        'Daily standup driver — start here. Risks/Actions panel highlights bottleneck spools and PR shortfalls.',
        [
          'Read the headline KPIs: total spools, AG/UG split, fabrication & erection %.',
          'Look at allocation status donut — chase any UNALLOCATED / PARTIAL_SOFT lines.',
          'Click any risk card → drilldown of impacted spools with deep-links into Workfront Status tab.',
          'Open RAID/Trends widgets to verify against project narrative.',
        ]),
      T('workfront', 'Workfront Status', LayoutGrid,
        'AG-Grid of every spool/line with its current allocation state, MTO vs picked, and shortfall.',
        '`pwm_workfronts` + live join into `mtmMaterials` to compute available vs reserved per material code.',
        'When chasing readiness for a specific area / unit / priority.',
        [
          'Use the global filter bar (AG/UG, Priority, Area, Status) to narrow the grid.',
          'Click any row → opens the Allocation Engine pre-filtered to that spool.',
          'Status legend: UNALLOCATED → PARTIAL_SOFT → READY_SOFT → PO_READY → PARTIAL_HARD → FULLY_ALLOCATED.',
          'Click the MTM badge on each material to see live inventory across warehouses.',
        ]),
      T('fabrication', 'Fabrication Progress', Hammer,
        'Stage-gate-wise fabrication progress (Fit-Up → Welding → NDT → PWHT → Hydro).',
        '`pwm_fab_progress` collection + per-spool Inch-Meter percentage roll-ups.',
        'Used by fab shop leads to capture daily progress + chase rework.',
        [
          'Filter by area/unit/priority via the global bar.',
          'Click into a spool row to see the 5 gate timestamps + photos + welder QR scans.',
          'Bulk update by uploading the standard Fab Daily template (CSV/Excel).',
          'Stage-gate completion auto-cascades into Erection readiness state.',
        ]),
      T('erection', 'Erection Progress', CableCar,
        'Site erection tracking using Inch-Meter (industry standard for piping productivity).',
        '`pwm_erection_progress` collection — Inch-Meter and Joint counts per line.',
        'Used by site supervisors to log daily erection and reconcile against fab dispatch.',
        [
          'Pick a line → record IM completed today, photo & joint count.',
          'Lines flip to ERECTED when % ≥ 100 — auto-feeds Commissioning Schedule.',
          'Variance vs planned IM-rate shows up as a Risk on the Summary tab.',
        ]),
      T('3dmodel', '3D Model', Box,
        'the Panorix 3D viewer for the integrated 3D plant model with workfront colour heatmap.',
        'Forge model URN (uploaded once) + colour overlay driven by spool allocation status + MTM stockout.',
        'When you want to spatially understand where the bottlenecks are sitting.',
        [
          'Use the Heatmap selector to overlay: Allocation State / Fab % / Erection % / MTM Stockout.',
          'Click any element → opens an MTM popover with live inventory for that spool\'s materials.',
          'Camera bookmarks (future) let you save area-specific views per project.',
        ]),
      T('allocation', 'Allocation Engine', Cog,
        'Auto-allocate stock to workfronts using priority × area × need-by-date rules.',
        'Pulls READY workfront demand + `mtmMaterials.inventory.availableQuantity`; writes `inventory.reservedQuantity` on commit.',
        'When new GRN arrives or when re-balancing priorities mid-project.',
        [
          'Choose target: by Priority (P1 first), by Area, or specific workfront IDs.',
          'Click Preview → see proposed soft-allocations row-by-row.',
          'Commit → atomic $inc on `mtmMaterials.inventory.reservedQuantity`; flips status to FULLY_ALLOCATED / PARTIAL_HARD.',
          'Roll-back is available within the audit window if you commit by mistake.',
        ]),
      T('import', 'Data Import', FileUp,
        'Excel/CSV bulk import for spools, lines, MTO, and progress updates.',
        'Standardized Excel templates parsed server-side and validated against schema.',
        'During initial project on-boarding or when EPC issues a revised IFC.',
        [
          'Download the template → fill it offline → upload.',
          'Validation panel shows row-by-row errors + a "fix-and-retry" CSV.',
          'Successful rows commit to `pwm_workfronts` and trigger an allocation refresh.',
        ]),
      T('analytics', 'KPIs & Analytics', BarChart3,
        'Trend & comparison charts: Spool throughput, IM/day, allocation lead time, vendor split.',
        'Aggregations over `pwm_workfronts` + `pwm_fab_progress` + `pwm_erection_progress`.',
        'When preparing PMO decks or weekly steering reviews.',
        [
          'Switch between Plan vs Actual, Earned vs Burnt, and Cycle-Time tabs.',
          'Export-as-PNG button for slide-ready visuals.',
        ]),
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // Structural Workfront Management (SWM)
  // ─────────────────────────────────────────────────────────────────────────────
  swm: {
    moduleKey: 'swm',
    title: 'Structural Workfront Management',
    subtitle: 'Steel order → allocation → fabrication → erection lifecycle',
    color: '#F59206',
    accent: '#D97706',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Project-wide allocation health and fab/erect progress for structural steel.',
        '`swm_workfronts` + `mtmMaterials` (discipline=STRU) atomic counters.',
        'Daily ops view — open this first to find blocked marks/lifts.',
        [
          'Headline KPIs: total marks, tonnage, fab %, erect %.',
          'Cards under "Risks" surface shortfall lifts and overdue NDT.',
          'Drill into any KPI → opens grid pre-filtered.',
        ]),
      T('workfront', 'Workfront Status', LayoutGrid,
        'Grid of every structural mark with allocation state and shortfall by section.',
        '`swm_workfronts` + live `mtmMaterials` join (section/grade/length/tonnage).',
        'When chasing a specific lift or grid line.',
        [
          'Filter by area / priority / status using global bar.',
          'Each row links to Allocation Engine with section/grade pre-selected.',
          'The "Reserved" badge mirrors `mtmMaterials.inventory.reservedQuantity`.',
        ]),
      T('fabrication', 'Fabrication', Hammer,
        'Shop fabrication tracking per mark (cutting → fitting → welding → painting).',
        '`swm_fab_progress`, with QA & paint sign-off stored per mark.',
        'For fab shop daily reporting.',
        [
          'Update gate completion + photo evidence per mark.',
          'Marks become "Ready for Dispatch" only when paint is signed off.',
        ]),
      T('erection', 'Erection', CableCar,
        'Site erection tracking by lift sequence with bolt-torque & survey sign-offs.',
        '`swm_erection_progress` collection + lift sequencing per area.',
        'For site engineering teams to record daily erected tonnage and survey results.',
        [
          'Pick a lift → record erected tonnage, photos, torque & survey IDs.',
          'Lift completion auto-marks the mark as ERECTED.',
        ]),
      T('3dmodel', '3D Model', Box,
        '3D viewer with structural heatmap and per-element MTM popover.',
        'Forge URN + colour rules driven by SWM allocation + MTM stockout flags.',
        'For spatial walkthroughs and steering reviews.',
        [
          'Toggle heatmap modes via the legend control.',
          'Click any element → MTM popover with live availability.',
        ]),
      T('allocation', 'Allocation Engine', Cog,
        'Bulk allocate stocked sections to lifts per priority/area.',
        '`swm_workfronts` demand vs `mtmMaterials` availability; commits via atomic $inc.',
        'When new GRN of structural steel arrives.',
        [
          'Pick scope (priority/area) → Preview → Commit.',
          'Rollback available in the audit window.',
        ]),
      T('import', 'Data Import', FileUp,
        'Excel import for marks / MTO / lift sequence.',
        'Standardized templates with schema validation.',
        'During on-boarding or after a major EPC revision.',
        ['Download → fill → upload; row-level errors are shown inline.']),
      T('validation', 'Validation', ShieldCheck,
        'Data quality checks — duplicate marks, orphan tonnage, missing grade/section.',
        'In-memory diff between imported data and existing `swm_workfronts`.',
        'After any bulk import.',
        ['Resolve all RED issues before allocation runs accept the data.']),
      T('analytics', 'KPIs & Analytics', BarChart3,
        'Tonnage curves (S-curve), throughput, allocation lead-time, vendor mix.',
        'Aggregations on `swm_workfronts` + progress collections.',
        'For weekly review and steering committee.',
        ['Export to PNG for slide decks; switch Plan/Actual toggle.']),
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // Procurement Schedule (MCI)
  // ─────────────────────────────────────────────────────────────────────────────
  procurement: {
    moduleKey: 'procurement',
    title: 'Procurement Schedule',
    subtitle: 'PR → RFQ → PO → Vendor Drawing → MFG → FAT → Dispatch → GRN',
    color: '#7C3AED',
    accent: '#6D28D9',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Procurement pipeline overview with stage-wise counts and lateness against ROS dates.',
        '`mci_packages` + `mci_prs` + `mci_pos` collections with computed Required-on-Site (ROS) deltas.',
        'Daily PMO standup — call out PRs aging in DRAFT, POs slipping FAT.',
        [
          'Headline strip: total packages, awarded value, in-pipeline value, slipping count.',
          'Drill into any Risk → grid of impacted packages with deep-links.',
          'Use the timeline lens to see week-by-week ROS vs Planned Dispatch.',
        ]),
      T('packages', 'Packages', LayoutGrid,
        'Package master with scope, vendor, value, current stage, ROS date.',
        '`mci_packages` collection.',
        'When creating a new package or updating stage milestones.',
        [
          'Click + → create a package (scope, vendor, value, ROS).',
          'Click a row → opens the package detail with stage timeline.',
          'Awarded packages auto-feed Cost / Commitments / Cash Flow modules.',
        ]),
      T('prs', 'Purchase Requisitions', FileText,
        'PR pipeline — DRAFT → PENDING_APPROVAL → APPROVED → CONVERTED_TO_PO.',
        '`mci_prs` collection. MTM auto-PR feeds drop in here as DRAFT.',
        'When converting demand into PRs and walking them through approvals.',
        [
          'Auto-PRs from MTM Reorder appear with a "MTM" tag — review & approve.',
          'Bulk-approve up to your role threshold; high-value PRs route to PM/Sponsor.',
        ]),
      T('pos', 'Purchase Orders', Receipt,
        'PO pipeline — DRAFT → ISSUED → PARTIAL_GRN → CLOSED.',
        '`mci_pos` collection; GRN events update line-level received quantity.',
        'When issuing POs and tracking dispatch & GRN.',
        [
          'Issuing a PO snapshots line items and locks scope.',
          'Each GRN against a PO line increments `mtmMaterials.inventory.grnQuantity`.',
          'POs with zero GRN past ROS are flagged as RED risks on Summary.',
        ]),
      T('vendor-drawings', 'Vendor Drawings', FileSpreadsheet,
        'Vendor-side deliverables for awarded packages with approval cycle.',
        '`mci_vendor_drawings` — IFA/IFC submissions per package.',
        'When approving submissions and tracking re-submissions.',
        ['Late vendor drawings cascade into fab/erection slippage — auto-surfaced as risks.']),
      T('expediting', 'Expediting', Truck,
        'Vendor expediting log: weekly status calls, milestone evidence, dispatch readiness.',
        '`mci_expediting` records with photo evidence and slippage notes.',
        'Weekly cadence for the expeditor + buyer team.',
        ['Past-due dispatch risk auto-feeds the Site Material Planning forecast.']),
      T('analytics', 'KPIs & Analytics', BarChart3,
        'Cycle-time KPIs by stage, on-time-PO %, vendor scorecard, savings vs budget.',
        'Aggregations on `mci_packages` + `mci_pos` + `mci_prs`.',
        'For monthly executive reviews and vendor reviews.',
        ['Export PNG; toggle Plan/Actual; vendor leaderboard sorts by on-time-delivery.']),
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // Construction Schedule
  // ─────────────────────────────────────────────────────────────────────────────
  construction: {
    moduleKey: 'construction',
    title: 'Construction Schedule',
    subtitle: 'WBS-based plan with progress, slippage and CPI tracking',
    color: '#0EA5E9',
    accent: '#0284C7',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Project-level construction KPIs — SPI/CPI, % complete, milestones at risk.',
        '`construction_schedule` activities + earned value calc against baseline.',
        'For daily project controls.',
        [
          'Look at the milestone tile first — any RED milestone means a re-baseline conversation.',
          'Drill into SPI dip → opens schedule grid filtered to slipping activities.',
        ]),
      T('schedule', 'Schedule', Calendar,
        'WBS-based activity grid with start/finish/dur/% complete and predecessor links.',
        '`construction_schedule_activities` collection.',
        'When editing dates, dependencies, or recording physical progress.',
        [
          'Inline-edit % complete; the SPI updates immediately.',
          'Bulk import via the standard P6/MSP CSV template.',
        ]),
      T('gantt', 'Gantt', Activity,
        'Visual Gantt chart with critical path overlay and float bands.',
        'Same data as Schedule tab, rendered with d3-timelines.',
        'For visual reviews and stakeholder walkthroughs.',
        ['Zoom + collapse summary tasks; toggle critical-path-only mode.']),
      T('progress', 'Progress', TrendingDown,
        'Physical progress capture against measurement methods (units, area, IM).',
        '`construction_progress_log` entries — one per activity per day.',
        'Daily site team uses this to log measured progress with photos.',
        ['CSV bulk uploads supported; photos store in object storage.']),
      T('analytics', 'KPIs & Analytics', BarChart3,
        'S-curves (Plan/Earned/Burnt), SPI/CPI trend, milestone burndown.',
        'Earned-value snapshots stored daily.',
        'For weekly PMO reviews.',
        ['Snapshots are timestamped; you can compare any two reporting periods.']),
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // Engineering Schedule
  // ─────────────────────────────────────────────────────────────────────────────
  engineering: {
    moduleKey: 'engineering',
    title: 'Engineering Schedule',
    subtitle: 'Discipline-wise deliverable plan (IFA/IFC) and progress',
    color: '#10B981',
    accent: '#059669',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Engineering progress vs plan by discipline (Process, Piping, Mech, Civil, E&I).',
        '`engineering_deliverables` collection with IFA/IFC milestones per doc.',
        'Daily / weekly PMO view.',
        [
          'Headline: total deliverables, IFA on-time %, IFC on-time %.',
          'Any RED card click drills into late deliverables.',
        ]),
      T('deliverables', 'Deliverables', FileText,
        'Master register: doc no., discipline, type, IFA/IFC dates, current status.',
        '`engineering_deliverables` collection.',
        'For tracking individual document progress and submissions.',
        [
          'Bulk import or create individual.',
          'Each row links to vendor drawings (if outsourced).',
        ]),
      T('disciplines', 'Disciplines', Briefcase,
        'Per-discipline progress dashboard with manhour burn vs deliverable burn.',
        'Aggregated `engineering_deliverables` + `engineering_timesheet` (if loaded).',
        'For discipline lead reviews.',
        ['Compare disciplines side-by-side; surface manhour overruns.']),
      T('analytics', 'KPIs & Analytics', BarChart3,
        'IFA cycle time, rework rate (revisions), discipline throughput.',
        'Aggregations on `engineering_deliverables`.',
        'For lessons-learned and benchmarking sessions.',
        []),
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // Commissioning Schedule
  // ─────────────────────────────────────────────────────────────────────────────
  commissioning: {
    moduleKey: 'commissioning',
    title: 'Commissioning Schedule',
    subtitle: 'Sub-system → System → Hand-over (MC → PC → SU) progression',
    color: '#EAB308',
    accent: '#CA8A04',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Sub-system-wise commissioning readiness and punch-list health.',
        '`commissioning_subsystems` + `commissioning_punch_lists`.',
        'Daily — see what is MC-ready and what is being blocked by punch items.',
        ['Headline ribbon: MC complete, PC complete, SU complete counts.', 'Risks: A-cat punch items > target.']),
      T('subsystems', 'Sub-Systems', LayoutGrid,
        'Sub-system master with MC / PC / SU dates and dependencies.',
        '`commissioning_subsystems` collection.',
        'For commissioning manager weekly review.',
        ['Click a sub-system → see its MC checklist + punch items.']),
      T('punchlist', 'Punch Lists', ClipboardCheck,
        'A/B/C category punch items per sub-system.',
        '`commissioning_punch_lists` collection.',
        'Daily punch-out by walk-down teams.',
        ['A-cat items block MC; B/C can be cleared post-MC.']),
      T('analytics', 'KPIs & Analytics', BarChart3,
        'Punch close rate, MC throughput, hand-over forecast curve.',
        'Aggregations on punch list + sub-system collections.',
        'For executive walkthrough decks.',
        []),
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // RAID Dashboard
  // ─────────────────────────────────────────────────────────────────────────────
  raid: {
    moduleKey: 'raid',
    title: 'RAID Dashboard (Risks · Assumptions · Issues · Dependencies)',
    subtitle: 'Single pane for project risk hygiene',
    color: '#DC2626',
    accent: '#B91C1C',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'RAID heatmap — open count by category × severity × age.',
        '`raid_items` collection with severity, owner, due date, mitigation log.',
        'Weekly RAID meeting.',
        ['Top 5 risks pinned by score (impact × probability × age).']),
      T('risks', 'Risks', AlertOctagon,
        'Forward-looking exposures awaiting mitigation.',
        '`raid_items` where type=RISK.',
        'When raising/closing risks during weekly meetings.',
        ['Mitigation log captures action history; auto-archives at closure.']),
      T('issues', 'Issues', ShieldAlert,
        'Realized risks that need active resolution.',
        '`raid_items` where type=ISSUE.',
        'For ops standups and accountability tracking.',
        []),
      T('assumptions', 'Assumptions', BookOpen,
        'Documented project assumptions and their validity status.',
        '`raid_items` where type=ASSUMPTION.',
        'At project initiation; revisited at major gates.',
        []),
      T('dependencies', 'Dependencies', GitBranch,
        'Inter-module and external dependencies tracker.',
        '`raid_items` where type=DEPENDENCY with linked module/external entity.',
        'When mapping vendor / inter-discipline dependencies.',
        []),
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // Cost / Budget / WCM / EVM / Invoices / Commitments / Contracts / Tender
  // ─────────────────────────────────────────────────────────────────────────────
  cost: {
    moduleKey: 'cost',
    title: 'Cost Management Summary',
    subtitle: 'Estimate-at-Complete vs Budget — single source of cost truth',
    color: '#22C55E',
    accent: '#16A34A',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'CBS-rolled EAC vs Budget vs Actuals; variance heat strip.',
        '`cbs_matrix` rolled by category × WBS × commitment posture.',
        'Weekly cost review.',
        ['Watch the EAC variance lane — drill any RED cell to PRs/POs feeding the cost code.']),
      T('matrix', 'CBS Matrix', LayoutGrid,
        'Cost Breakdown Structure matrix: budget, committed, accrued, paid by cost code.',
        '`cbs_matrix` rolled from `mci_pos` + `invoices` + `commitments`.',
        'When recasting budgets or chasing accruals.',
        []),
      T('analytics', 'Analytics', BarChart3,
        'CPI trend, cost overrun by category, vendor cost mix.',
        'Aggregations on cost feeds.',
        'For Director-level financial reviews.',
        []),
    ],
  },

  budget: {
    moduleKey: 'budget',
    title: 'Budget Revision Tracker',
    subtitle: 'Original → Approved Revisions → Current baseline',
    color: '#0EA5E9',
    accent: '#0369A1',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Budget revision history & cumulative drift from sanction.',
        '`budget_revisions` collection with approval workflow.',
        'Quarterly budget reviews.',
        []),
      T('revisions', 'Revisions', Repeat,
        'Each revision with line items, justification, approver chain.',
        '`budget_revisions` documents.',
        'When raising or approving a revision.',
        ['Approved revisions auto-update CBS Matrix baselines.']),
    ],
  },

  evm: {
    moduleKey: 'evm',
    title: 'Earned Value Management',
    subtitle: 'PV, EV, AC, SPI, CPI — the EVM fundamentals',
    color: '#8B5CF6',
    accent: '#7C3AED',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Project-level EVM dashboard with S-curves and indices.',
        'Daily snapshots from `evm_snapshots`.',
        'Weekly project review.',
        []),
      T('curves', 'S-Curves', Activity,
        'PV/EV/AC over time with forecast overlay.',
        '`evm_snapshots` time-series.',
        'For trend reviews.',
        []),
    ],
  },

  invoices: {
    moduleKey: 'invoices',
    title: 'Invoice Management',
    subtitle: 'Vendor invoice ingestion → approval → payment',
    color: '#06B6D4',
    accent: '#0891B2',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Invoice ageing, awaiting approval, payable forecast.',
        '`invoices` collection.',
        'Weekly AP review.',
        []),
      T('register', 'Register', FileText,
        'Master register of invoices with PO link, GRN match, approval status.',
        '`invoices` collection.',
        'Daily AP processing.',
        []),
    ],
  },

  commitments: {
    moduleKey: 'commitments',
    title: 'Commitment Tracking',
    subtitle: 'Awarded value vs accrued vs paid across PO/contracts',
    color: '#F472B6',
    accent: '#DB2777',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Total commitments vs accruals vs cash out, with aging buckets.',
        '`mci_pos` + `contracts` aggregated.',
        'Monthly cash committee.',
        []),
    ],
  },

  contracts: {
    moduleKey: 'contracts',
    title: 'Contracts Module',
    subtitle: 'Master contracts, milestones, claims, change orders',
    color: '#84CC16',
    accent: '#65A30D',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Contracts portfolio view — value, milestones at risk, open change orders.',
        '`contracts` + `contract_milestones` + `contract_change_orders`.',
        'Weekly contracts review.',
        []),
      T('list', 'Contracts', FileText,
        'List of all contracts with value, vendor, period, current status.',
        '`contracts` collection.',
        'When raising or closing contracts.',
        []),
    ],
  },

  tender: {
    moduleKey: 'tender',
    title: 'Tender Analytics',
    subtitle: 'Bidder pool, evaluation, award analytics',
    color: '#A78BFA',
    accent: '#8B5CF6',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Tender pipeline + award savings rate.',
        '`tenders` and bid evaluation collections.',
        'Monthly procurement steering.',
        []),
    ],
  },

  wcm: {
    moduleKey: 'wcm',
    title: 'Working Capital Management',
    subtitle: 'Cash inflow/outflow forecast and working-capital efficiency',
    color: '#14B8A6',
    accent: '#0F766E',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Cash flow forecast, DSO/DPO, working-capital tied to commitments.',
        '`wcm_cashflow` + invoice/po aggregations.',
        'Monthly treasury review.',
        []),
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // Equipment / Manpower / HSE / Site Monitoring / Progress Reports
  // ─────────────────────────────────────────────────────────────────────────────
  equipment: {
    moduleKey: 'equipment',
    title: 'Equipment Management',
    subtitle: 'Plant & equipment register, utilization, breakdown log',
    color: '#F97316',
    accent: '#EA580C',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Fleet utilization, downtime, hire cost per asset class.',
        '`equipment_master` + `equipment_log` collections.',
        'Weekly plant manager review.',
        []),
      T('register', 'Register', LayoutGrid,
        'Asset register: type, owner (own/hired), location, condition.',
        '`equipment_master`.',
        'On-boarding and reassignment.',
        []),
      T('log', 'Daily Log', Activity,
        'Per-day per-asset hours, downtime, fuel/lube.',
        '`equipment_log`.',
        'Daily site capture.',
        []),
    ],
  },

  manpower: {
    moduleKey: 'manpower',
    title: 'Manpower Management',
    subtitle: 'Headcount plan vs deployed, by trade & contractor',
    color: '#3B82F6',
    accent: '#2563EB',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Plan vs Actual headcount by trade, with productivity (units/manhour).',
        '`manpower_log` + planning curves.',
        'Daily / weekly leadership.',
        []),
      T('roster', 'Roster', Users,
        'Per-contractor per-trade daily roster.',
        '`manpower_log`.',
        'Daily site capture.',
        []),
    ],
  },

  hse: {
    moduleKey: 'hse',
    title: 'HSE Dashboard',
    subtitle: 'Safety, environment, health — leading & lagging indicators',
    color: '#EF4444',
    accent: '#DC2626',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'LTIFR, TRIFR, near-miss count, observation throughput, environmental incidents.',
        '`hse_incidents` + `hse_observations` + manhour denominators.',
        'Daily safety stand-down + weekly HSE committee.',
        []),
      T('incidents', 'Incidents', AlertOctagon,
        'Recordable incidents with severity, RCA, corrective actions.',
        '`hse_incidents`.',
        'When logging or closing incidents.',
        []),
      T('observations', 'Observations', Eye,
        'Behavioural observations & near-miss capture.',
        '`hse_observations`.',
        'Daily by HSE field officers.',
        []),
    ],
  },

  site: {
    moduleKey: 'site',
    title: 'Site Monitoring',
    subtitle: '360° photos, drone progress, CCTV events',
    color: '#06B6D4',
    accent: '#0E7490',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Site progress photo timeline + drone overlay vs schedule.',
        '`site_media` + `drone_runs` collections (object storage backed).',
        'Weekly executive walkthroughs.',
        []),
    ],
  },

  progress: {
    moduleKey: 'progress',
    title: 'Progress Reports',
    subtitle: 'DPR / WPR / MPR auto-generation',
    color: '#84CC16',
    accent: '#65A30D',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Latest DPR/WPR/MPR + history archive.',
        'Generated from aggregated KPIs across PWM/SWM/Construction/HSE.',
        'Daily / Weekly / Monthly cadence.',
        []),
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // Scheduling / Gantt / Trends / 4D / 5D / Scan-To-Model
  // ─────────────────────────────────────────────────────────────────────────────
  scheduling: {
    moduleKey: 'scheduling',
    title: 'Scheduling Dashboard',
    subtitle: 'Cross-discipline integrated schedule overview',
    color: '#3B82F6',
    accent: '#1D4ED8',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Schedule health across Engineering / Procurement / Construction / Commissioning.',
        'Rolled from each module\'s SPI/CPI snapshot.',
        'Daily PMO standup.',
        []),
      T('rollup', 'Schedule Roll-up', Layers,
        'Master roll-up of milestones across modules.',
        'Aggregations across all schedule sources.',
        'For executive reviews.',
        []),
      T('gantt', 'Gantt', Activity,
        'Integrated cross-module Gantt with critical path.',
        'Same source as roll-up.',
        'For visual reviews.',
        []),
    ],
  },

  fourD: {
    moduleKey: 'fourD',
    title: '4D Modelling',
    subtitle: '3D model × schedule = time-phased build visualization',
    color: '#A855F7',
    accent: '#9333EA',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Timeline-driven 3D playback. Verify constructability sequence.',
        'Forge model URN + linked schedule activities + element-mapping.',
        'When reviewing constructability and sequencing.',
        ['Camera bookmarks (future) save area-specific views.']),
    ],
  },

  fiveD: {
    moduleKey: 'fiveD',
    title: '5D Modelling',
    subtitle: '4D + cost = time-phased cost visualization',
    color: '#EC4899',
    accent: '#DB2777',
    tabs: [
      T('summary', 'Summary', BarChart3,
        '5D playback with per-element cost overlay.',
        '4D base + CBS mapping per element.',
        'For executive cost-and-schedule walkthroughs.',
        []),
    ],
  },

  scanToModel: {
    moduleKey: 'scanToModel',
    title: 'Scan to Model',
    subtitle: 'Reality-capture (LiDAR / photogrammetry) → as-built comparison',
    color: '#22C55E',
    accent: '#16A34A',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Scan registrations and deviation reports vs design model.',
        '`scan_jobs` collection + cloud-processed deviation maps.',
        'After major site walks; before QA sign-off.',
        []),
    ],
  },

  trends: {
    moduleKey: 'trends',
    title: 'Trends',
    subtitle: 'Project narrative + AI-generated executive briefs',
    color: '#0EA5E9',
    accent: '#0369A1',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Cross-module trend analysis and AI briefings.',
        'Aggregates from all major modules; LLM-summarized.',
        'For executive prep before steering committee.',
        []),
    ],
  },

  aiBrief: {
    moduleKey: 'aiBrief',
    title: 'AI Brief',
    subtitle: 'Generative narrative of project state',
    color: '#8B5CF6',
    accent: '#7C3AED',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'On-demand AI executive brief using current KPIs.',
        'Cognara AI wrapped in `llm_service.py` — fed current project KPIs.',
        'Before any board meeting.',
        ['Brief covers cost, schedule, risk in 3 paragraphs.']),
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // Portfolio / Project Detail / Dashboard / Project Controls
  // ─────────────────────────────────────────────────────────────────────────────
  portfolio: {
    moduleKey: 'portfolio',
    title: 'Portfolio Map',
    subtitle: 'Multi-project portfolio with geo-map and KPI tiles',
    color: '#143A6B',
    accent: '#1E4E8C',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Portfolio-level KPIs + map view of all projects.',
        'Aggregations across `projects` collection + KPI feeds.',
        'For C-suite and program directors.',
        []),
    ],
  },

  controls: {
    moduleKey: 'controls',
    title: 'Project Controls',
    subtitle: 'Integrated controls cockpit (Schedule + Cost + Risk)',
    color: '#0EA5E9',
    accent: '#0369A1',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Combined SPI/CPI/Risk indices + trend bands.',
        'Roll-up of EVM, RAID, and EAC.',
        'Weekly project controls review.',
        []),
    ],
  },

  benchmarking: {
    moduleKey: 'benchmarking',
    title: 'Benchmarking',
    subtitle: 'Inter-project KPI comparison',
    color: '#F472B6',
    accent: '#DB2777',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'Compare KPIs across projects in the portfolio.',
        'Snapshots from each project\'s KPI feeds.',
        'For lessons-learned and benchmark studies.',
        []),
    ],
  },

  alerts: {
    moduleKey: 'alerts',
    title: 'Alerts (Time-Bar)',
    subtitle: 'Cross-module time-critical alerts',
    color: '#EF4444',
    accent: '#DC2626',
    tabs: [
      T('summary', 'Summary', BarChart3,
        'All active alerts grouped by severity + age.',
        '`alerts` collection from all module engines (incl. MTM stockout, schedule slip).',
        'Always-on — alerts ping the top-bar Siren icon.',
        ['Click any alert → opens its source module pre-filtered to the offending entity.']),
    ],
  },
};

// Convenience lookup
export const getModuleGuide = key => MODULE_GUIDES[key] || null;

// List of all module keys in a stable order (for the global "all modules" index page)
export const MODULE_KEYS = Object.keys(MODULE_GUIDES);
